/**
 * ONE BONE Fit Finder — the single sizing engine for the theme.
 *
 * Replaces the legacy `size-calculator.js` dropdown engine and the per-section
 * copies of the sizing matrix that used to live in onebone-size-calculator,
 * onebone-bottoms-size-calculator (section + block), onebone-room-shopper and
 * onebone-fit-lock.
 *
 * Public surface (window.OneBoneFit):
 *   recommendTop(inches, pounds)  -> '-2'..'7'
 *   recommendBottom(waistInches)  -> 'A'..'H'
 *   waistRangeFor(size)           -> { min, max }
 *   getProfile()                  -> { height, weight, waist, top, bottom, units, source }
 *   setProfile(patch)             -> persists + fires 'onebone:fit:change'
 *   clearProfile()
 *   saveToAccount()               -> Promise<{ ok, reason }>
 *   applyToProduct({ initial })   -> selects the saved size on a PDP
 *   open() / close()              -> the global Find My Fit popup
 *   format*                       -> unit-aware display helpers
 *
 * Anywhere the theme editor offers a URL field and no way to add markup, a
 * link to `#find-my-fit` opens the same popup the header does (OB-264).
 *
 * The tops matrix below is a verified 1:1 port of the 7x34 lookup that the
 * legacy `calculatorSizeTop` switch encoded — all 238 cells were diffed.
 */
(() => {
  'use strict';

  if (window.OneBoneFit) return;

  /* ------------------------------------------------------------------ data */

  // Rows are weight buckets, columns are height buckets (see HEIGHT_BREAKS).
  const TOPS_MATRIX = [
    { max: 179, sizes: ['-2', '-2', '-2', '-1', '-1', '-1', '-1'] }, // <=179 lb
    { max: 189, sizes: ['-1', '-1', '-1', '-1', '-1', '-1', '-1'] }, // 180-189 lb
    { max: 199, sizes: ['0', '0', '-1', '-1', '-1', '-1', '-1'] }, // 190-199 lb
    { max: 209, sizes: ['0', '0', '0', '0', '0', '0', '0'] }, // 200-209 lb
    { max: 219, sizes: ['0', '0', '0', '0', '0', '0', '0'] }, // 210-219 lb
    { max: 229, sizes: ['0', '0', '0', '0', '0', '0', '0'] }, // 220-229 lb
    { max: 239, sizes: ['0', '0', '0', '0', '0', '0', '0'] }, // 230-239 lb
    { max: 249, sizes: ['0', '0', '0', '0', '0', '0', '0'] }, // 240-249 lb
    { max: 259, sizes: ['1', '1', '1', '0', '0', '0', '0'] }, // 250-259 lb
    { max: 269, sizes: ['1', '1', '1', '1', '1', '1', '1'] }, // 260-269 lb
    { max: 279, sizes: ['2', '1', '1', '1', '1', '1', '1'] }, // 270-279 lb
    { max: 289, sizes: ['2', '2', '1', '1', '1', '1', '1'] }, // 280-289 lb
    { max: 299, sizes: ['2', '2', '1', '1', '1', '1', '1'] }, // 290-299 lb
    { max: 309, sizes: ['2', '2', '1', '1', '1', '1', '1'] }, // 300-309 lb
    { max: 319, sizes: ['3', '3', '2', '2', '2', '1', '1'] }, // 310-319 lb
    { max: 329, sizes: ['3', '3', '2', '2', '2', '2', '2'] }, // 320-329 lb
    { max: 339, sizes: ['3', '3', '3', '3', '3', '3', '3'] }, // 330-339 lb
    { max: 349, sizes: ['4', '4', '3', '3', '3', '3', '3'] }, // 340-349 lb
    { max: 359, sizes: ['4', '4', '3', '3', '3', '3', '3'] }, // 350-359 lb
    { max: 369, sizes: ['4', '4', '4', '4', '4', '4', '4'] }, // 360-369 lb
    { max: 379, sizes: ['4', '4', '4', '4', '4', '4', '4'] }, // 370-379 lb
    { max: 389, sizes: ['5', '5', '4', '4', '4', '3', '3'] }, // 380-389 lb
    { max: 399, sizes: ['5', '5', '5', '4', '4', '4', '4'] }, // 390-399 lb
    { max: 409, sizes: ['5', '5', '5', '5', '5', '5', '5'] }, // 400-409 lb
    { max: 419, sizes: ['5', '5', '5', '5', '5', '5', '5'] }, // 410-419 lb
    { max: 429, sizes: ['5', '5', '5', '5', '5', '5', '5'] }, // 420-429 lb
    { max: 439, sizes: ['6', '5', '5', '5', '5', '5', '5'] }, // 430-439 lb
    { max: 449, sizes: ['6', '6', '6', '6', '6', '6', '6'] }, // 440-449 lb
    { max: 459, sizes: ['6', '6', '6', '6', '6', '6', '6'] }, // 450-459 lb
    { max: 469, sizes: ['6', '6', '6', '6', '6', '6', '6'] }, // 460-469 lb
    { max: 479, sizes: ['6', '6', '6', '6', '6', '6', '6'] }, // 470-479 lb
    { max: 489, sizes: ['6', '6', '6', '6', '6', '6', '6'] }, // 480-489 lb
    { max: 499, sizes: ['7', '7', '7', '7', '7', '7', '7'] }, // 490-499 lb
    { max: Infinity, sizes: ['7', '7', '7', '7', '7', '7', '7'] }, // 500+ lb
  ];

  // Height bucket upper bounds in inches; index matches the matrix columns.
  const HEIGHT_BREAKS = [68, 71, 74, 77, 80, 83];

  // Waist ranges from the ONE BONE size guide matrix (the authoritative sheet).
  const BOTTOMS_RANGES = [
    { min: 29, max: 32, size: 'A' },
    { min: 33, max: 36, size: 'B' },
    { min: 37, max: 39, size: 'C' },
    { min: 40, max: 44, size: 'D' },
    { min: 45, max: 49, size: 'E' },
    { min: 50, max: 54, size: 'F' },
    { min: 55, max: 59, size: 'G' },
    { min: 60, max: 65, size: 'H' }
  ];

  const LIMITS = {
    height: { min: 61, max: 84, step: 1, fallback: 72 },
    weight: { min: 140, max: 500, step: 10, fallback: 260 },
    waist: { min: 29, max: 65, step: 1, fallback: 40 }
  };

  const KEYS = {
    height: 'oneboneFitHeightIn',
    weight: 'oneboneFitWeightLb',
    waist: 'oneboneFitWaistIn',
    units: 'oneboneFitUnits',
    top: 'sizeTop',
    bottom: 'sizeBottom',
    topManual: 'oneboneFitTopManual',
    bottomManual: 'oneboneFitBottomManual',
    savedAt: 'oneboneFitSavedAt'
  };

  const config = Object.assign(
    { loggedIn: false, loginUrl: '/account/login', accountUrl: '/account', saveEndpoint: '', profile: null },
    window.OneBoneFitConfig || {}
  );

  /* ----------------------------------------------------------- calculation */

  const clamp = (value, { min, max }) => Math.min(max, Math.max(min, value));

  const toInt = (value, fallback) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const heightColumn = (inches) => {
    const index = HEIGHT_BREAKS.findIndex((limit) => inches <= limit);
    return index === -1 ? HEIGHT_BREAKS.length : index;
  };

  const recommendTop = (inches, pounds) => {
    const height = clamp(toInt(inches, LIMITS.height.fallback), LIMITS.height);
    const weight = clamp(toInt(pounds, LIMITS.weight.fallback), LIMITS.weight);
    const row = TOPS_MATRIX.find((bucket) => weight <= bucket.max) || TOPS_MATRIX[TOPS_MATRIX.length - 1];
    return row.sizes[heightColumn(height)];
  };

  const recommendBottom = (waistInches) => {
    const waist = clamp(toInt(waistInches, LIMITS.waist.fallback), LIMITS.waist);
    const range = BOTTOMS_RANGES.find((bucket) => waist <= bucket.max);
    return (range || BOTTOMS_RANGES[BOTTOMS_RANGES.length - 1]).size;
  };

  const waistRangeFor = (size) =>
    BOTTOMS_RANGES.find((bucket) => bucket.size === String(size).toUpperCase()) || BOTTOMS_RANGES[0];

  /* ---------------------------------------------------------------- units */

  const UNIT_LABELS = {
    imperial: { height: 'ft/in', weight: 'lb', waist: 'in' },
    metric: { height: 'cm', weight: 'kg', waist: 'cm' }
  };

  const inchesToCm = (inches) => Math.round(inches * 2.54);
  const poundsToKg = (pounds) => Math.round(pounds * 0.45359237);

  const formatHeight = (inches, units) =>
    units === 'metric'
      ? `${inchesToCm(inches)} cm`
      : `${Math.floor(inches / 12)}'${inches % 12}"`;

  const formatWeight = (pounds, units) => {
    if (units === 'metric') {
      const kg = poundsToKg(pounds);
      return pounds >= LIMITS.weight.max ? `${kg} kg+` : `${kg} kg`;
    }
    return pounds >= LIMITS.weight.max ? `${pounds} lb+` : `${pounds} lb`;
  };

  const formatWaist = (inches, units) =>
    units === 'metric' ? `${inchesToCm(inches)} cm` : `${inches} in`;

  const formatWaistRange = (size, units) => {
    const { min, max } = waistRangeFor(size);
    return units === 'metric'
      ? `${inchesToCm(min)}–${inchesToCm(max)}`
      : `${min}–${max}`;
  };

  /* -------------------------------------------------------------- storage */

  const readStore = (key) => {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  };

  const writeStore = (key, value) => {
    try {
      if (value === null || value === undefined || value === '') {
        window.localStorage.removeItem(key);
      } else {
        window.localStorage.setItem(key, String(value));
      }
    } catch (error) {
      // Sizing still works without storage; nothing to recover.
    }
  };

  // The account copy wins on first load so a signed-in customer sees the same
  // fit on every device. Local edits then take over for the rest of the visit.
  let accountProfileApplied = false;

  const normaliseProfile = (raw) => {
    if (!raw) return null;
    const height = toInt(raw.height, NaN);
    const weight = toInt(raw.weight, NaN);
    const waist = toInt(raw.waist, NaN);
    if (!Number.isFinite(height) && !Number.isFinite(weight) && !Number.isFinite(waist)) return null;
    return {
      height: Number.isFinite(height) ? clamp(height, LIMITS.height) : LIMITS.height.fallback,
      weight: Number.isFinite(weight) ? clamp(weight, LIMITS.weight) : LIMITS.weight.fallback,
      waist: Number.isFinite(waist) ? clamp(waist, LIMITS.waist) : LIMITS.waist.fallback,
      units: raw.units === 'metric' ? 'metric' : 'imperial'
    };
  };

  const accountProfile = normaliseProfile(config.profile);

  const getProfile = () => {
    if (accountProfile && !accountProfileApplied) {
      accountProfileApplied = true;
      persist(accountProfile, 'account', { top: true, bottom: true });
    }

    const height = clamp(toInt(readStore(KEYS.height), LIMITS.height.fallback), LIMITS.height);
    const weight = clamp(toInt(readStore(KEYS.weight), LIMITS.weight.fallback), LIMITS.weight);
    const waist = clamp(toInt(readStore(KEYS.waist), LIMITS.waist.fallback), LIMITS.waist);
    const units = readStore(KEYS.units) === 'metric' ? 'metric' : 'imperial';

    // Tools that produce a size without measurements (the fit lock picks from
    // merchant-configured height bands) record it through setSizes(). That
    // explicit choice outranks a calculated one until the customer moves the
    // matching slider again, so the latest deliberate action always wins.
    const storedTop = readStore(KEYS.top);
    const storedBottom = readStore(KEYS.bottom);
    const useStoredTop = Boolean(storedTop) && (readStore(KEYS.topManual) === 'true' || !readStore(KEYS.height));
    const useStoredBottom = Boolean(storedBottom) && (readStore(KEYS.bottomManual) === 'true' || !readStore(KEYS.waist));
    const captured = Boolean(readStore(KEYS.height) || readStore(KEYS.waist) || storedTop || storedBottom);

    return {
      height,
      weight,
      waist,
      units,
      top: useStoredTop ? storedTop : recommendTop(height, weight),
      bottom: useStoredBottom ? storedBottom : recommendBottom(waist),
      captured,
      // A tops-only interaction never records a waist, so `bottom` above is a
      // default rather than an answer. These say which halves are real.
      hasTop: Boolean(storedTop),
      hasBottom: Boolean(storedBottom),
      savedToAccount: Boolean(readStore(KEYS.savedAt)) || Boolean(accountProfile),
      source: accountProfile ? 'account' : captured ? 'local' : 'default'
    };
  };

  const persist = (profile, reason, touched) => {
    const changed = touched || { top: true, bottom: true };

    writeStore(KEYS.units, profile.units);

    // Only record measurements the customer actually gave us. A bottoms-only
    // picker must not bank a default height and weight, or we would show a top
    // recommendation nobody asked for — and overwrite a real one.
    // sizeTop / sizeBottom stay the cross-theme contract: the rotation builder,
    // room shopper, fit lock and PDP variant selection all read these.
    if (changed.top) {
      writeStore(KEYS.height, profile.height);
      writeStore(KEYS.weight, profile.weight);
      writeStore(KEYS.top, recommendTop(profile.height, profile.weight));
      writeStore(KEYS.topManual, null);
    }
    if (changed.bottom) {
      writeStore(KEYS.waist, profile.waist);
      writeStore(KEYS.bottom, recommendBottom(profile.waist));
      writeStore(KEYS.bottomManual, null);
    }
    if (reason !== 'silent') {
      document.dispatchEvent(
        new CustomEvent('onebone:fit:change', { detail: Object.assign({ reason }, getProfile()) })
      );
    }
  };

  const setProfile = (patch, reason) => {
    const current = getProfile();
    const next = {
      height: clamp(toInt(patch.height, current.height), LIMITS.height),
      weight: clamp(toInt(patch.weight, current.weight), LIMITS.weight),
      waist: clamp(toInt(patch.waist, current.waist), LIMITS.waist),
      units: patch.units === 'metric' || patch.units === 'imperial' ? patch.units : current.units
    };
    // A patch that only carries a waist must not recalculate the top size, and
    // vice versa — pickers configured for one garment type send one half.
    persist(next, reason || 'update', {
      top: 'height' in patch || 'weight' in patch,
      bottom: 'waist' in patch
    });
    return getProfile();
  };

  /**
   * Record a size that was chosen rather than calculated (the fit lock's
   * height bands, a size-chart click). Keeps the header label, PDP preselect
   * and account summary in step without inventing measurements.
   */
  const setSizes = ({ top, bottom }) => {
    if (top) {
      writeStore(KEYS.top, top);
      writeStore(KEYS.topManual, 'true');
    }
    if (bottom) {
      writeStore(KEYS.bottom, bottom);
      writeStore(KEYS.bottomManual, 'true');
    }
    document.dispatchEvent(
      new CustomEvent('onebone:fit:change', { detail: Object.assign({ reason: 'sizes' }, getProfile()) })
    );
    return getProfile();
  };

  const clearProfile = () => {
    Object.values(KEYS).forEach((key) => writeStore(key, null));
    accountProfileApplied = true;
    document.dispatchEvent(new CustomEvent('onebone:fit:change', { detail: getProfile() }));
  };

  /* -------------------------------------------------------------- account */

  /**
   * Persist the fit profile against the customer record.
   *
   * An online-store theme cannot write customer metafields on its own, so this
   * posts to an app-proxy endpoint configured in theme settings
   * (`fit_profile_endpoint`). Until that endpoint exists the call resolves with
   * `{ ok: false, reason: 'no-endpoint' }` and the fit stays local — every
   * other part of the flow (capture, recall, PDP preselect, cart attributes)
   * works regardless. See OB-232.
   */
  const saveToAccount = async () => {
    const profile = getProfile();

    if (!config.loggedIn) return { ok: false, reason: 'not-logged-in' };
    if (!config.saveEndpoint) return { ok: false, reason: 'no-endpoint' };

    try {
      const response = await fetch(config.saveEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          height_in: profile.height,
          weight_lb: profile.weight,
          waist_in: profile.waist,
          top_size: profile.top,
          bottom_size: profile.bottom,
          units: profile.units
        })
      });
      if (!response.ok) return { ok: false, reason: `http-${response.status}` };
      writeStore(KEYS.savedAt, new Date().toISOString());
      return { ok: true };
    } catch (error) {
      return { ok: false, reason: 'network' };
    }
  };

  // Fit also rides along on the order so ops can see it without the app proxy.
  const saveToCart = async () => {
    const profile = getProfile();
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            'Fit — top size': profile.top,
            'Fit — bottom size': profile.bottom,
            'Fit — measurements': `${formatHeight(profile.height, profile.units)} / ${formatWeight(profile.weight, profile.units)} / ${formatWaist(profile.waist, profile.units)} waist`
          }
        })
      });
      return { ok: true };
    } catch (error) {
      return { ok: false, reason: 'network' };
    }
  };

  /* ------------------------------------------------- PDP variant selection */

  const selectVariant = (variantId, sizeOption) => {
    const setCartForms = (id) => {
      document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        if (input) input.value = id;
      });
    };

    if (!sizeOption) {
      setCartForms(variantId);
      return;
    }

    const radioId = sizeOption.getAttribute('for');
    const radio = radioId && document.getElementById(radioId);
    if (!radio) return;

    // Radio names in this theme contain newlines, so group lookups compare trimmed.
    const groupName = (radio.getAttribute('name') || '').trim();
    const group = groupName
      ? Array.from(document.querySelectorAll('input[type="radio"]')).filter(
          (input) => (input.getAttribute('name') || '').trim() === groupName
        )
      : [radio];

    // A sold-out recommendation falls through to the first available size.
    const unavailable = (input) => input.disabled || input.classList.contains('disabled');
    if (unavailable(radio)) {
      const next = group.find((input) => !unavailable(input));
      if (!next) return;
      const nextLabel = document.querySelector(`label[for="${next.id}"]`);
      const nextId = nextLabel && (nextLabel.dataset.variantProductId || nextLabel.dataset.variantId);
      if (nextLabel && nextId) selectVariant(nextId, nextLabel);
      return;
    }

    if (radio.checked && radio.getAttribute('data-value-selected') === 'true') return;

    group.forEach((input) => {
      const isTarget = input.id === radio.id;
      input.setAttribute('data-value-selected', isTarget ? 'true' : 'false');
      input.checked = isTarget;
    });

    const formId = radio.getAttribute('form');
    const form = formId && document.getElementById(formId);
    if (form) {
      const input = form.querySelector('input[name="id"]');
      if (input) input.value = variantId;
    }
    setCartForms(variantId);

    radio.dispatchEvent(new Event('change', { bubbles: true }));
    radio.dispatchEvent(new Event('input', { bubbles: true }));
  };

  /**
   * Preselect the stored size in this product's variant picker.
   *
   * `initial` marks the run that happens on page land rather than after the
   * customer changed their fit. A `?variant=` in the address bar is a
   * deliberate choice — an ad, a review link, a reorder — so the land-time run
   * leaves it alone; a fit captured or edited during the visit still wins.
   */
  const applyToProduct = ({ initial = false } = {}) => {
    const scope = document.querySelector('[data-ob-fit-product-scope]');
    if (!scope) return;

    if (initial && new URL(window.location.href).searchParams.get('variant')) return;

    const profile = getProfile();
    if (!profile.captured) return;

    const wanted = scope.dataset.obFitProductScope === 'bottom' ? profile.bottom : profile.top;
    // Stay inside this product's own section: a cart drawer or a second product
    // block on the page carries its own `.js-option-size` buttons.
    const picker = scope.closest('.shopify-section') || document;
    const option =
      picker.querySelector(`.js-option-size[data-size="${wanted}"]`) ||
      document.querySelector(`.js-option-size[data-size="${wanted}"]`);
    if (!option) return;

    const variantId = option.dataset.variantProductId || option.dataset.variantId;
    if (!variantId) return;

    selectVariant(variantId, option);

    if (window.history && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({ path: url.toString() }, '', url.toString());
    }
  };

  /* ---------------------------------------------------------------- popup */

  const POPUP_CLASS = 'popup-size--opening';

  /**
   * Why the picker was opened, for as long as it is open.
   *
   * The header's "Find My Fit" opens it with nothing to go back to, so it ends
   * on "shop your size". The cart's gift offer opens it mid-flow — there is a
   * free pair waiting on a size — so it ends on "Lock in My Size" and hands the
   * shopper back to the cart. Everything else about the picker is identical;
   * only the way out changes.
   */
  let openIntent = null;
  const getIntent = () => openIntent;

  const open = (options = {}) => {
    openIntent = (options && options.intent) || null;
    document.body.classList.add(POPUP_CLASS);
    document.dispatchEvent(new CustomEvent('onebone:fit:open', { detail: { intent: openIntent } }));
  };

  const close = () => {
    const intent = openIntent;
    openIntent = null;
    document.body.classList.remove(POPUP_CLASS);
    document.dispatchEvent(new CustomEvent('onebone:fit:close', { detail: { intent } }));
  };

  /* ----------------------------------------------------------------- init */

  window.OneBoneFit = {
    config,
    LIMITS,
    BOTTOMS_RANGES,
    TOPS_MATRIX,
    recommendTop,
    recommendBottom,
    waistRangeFor,
    getProfile,
    setProfile,
    setSizes,
    clearProfile,
    saveToAccount,
    saveToCart,
    applyToProduct,
    selectVariant,
    open,
    close,
    getIntent,
    formatHeight,
    formatWeight,
    formatWaist,
    formatWaistRange,
    unitLabels: UNIT_LABELS
  };

  // Section scripts are bundled separately and may run before this file, so
  // they wait on this instead of assuming window.OneBoneFit exists.
  document.dispatchEvent(new CustomEvent('onebone:fit:ready'));
})();

/**
 * <fit-finder> — the one picker. Every surface (header popup, PDP block,
 * landing-page sections, room shopper) renders `snippets/fit-finder.liquid`
 * inside this element and gets the same behaviour.
 */
(() => {
  'use strict';

  const Fit = window.OneBoneFit;
  if (!Fit || window.customElements.get('fit-finder')) return;

  class FitFinder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      this.mode = this.dataset.mode || 'both';
      this.inputs = {
        height: this.querySelector('[data-ob-fit-input="height"]'),
        weight: this.querySelector('[data-ob-fit-input="weight"]'),
        waist: this.querySelector('[data-ob-fit-input="waist"]')
      };

      this.bindInputs();
      this.bindUnits();
      this.bindAccount();

      this.onProfileChange = (event) => {
        if (event.detail && event.detail.origin === this) return;
        this.hydrate();
      };
      document.addEventListener('onebone:fit:change', this.onProfileChange);

      this.hydrate();
    }

    disconnectedCallback() {
      document.removeEventListener('onebone:fit:change', this.onProfileChange);
    }

    /* ------------------------------------------------------------ bindings */

    bindInputs() {
      Object.values(this.inputs).forEach((input) => {
        if (!input) return;
        input.addEventListener('input', () => this.commit());
      });
    }

    bindUnits() {
      this.querySelectorAll('[data-ob-fit-units]').forEach((button) => {
        button.addEventListener('click', () => {
          Fit.setProfile({ units: button.dataset.obFitUnits }, 'units');
          this.hydrate();
        });
      });
    }

    bindAccount() {
      // One button per account state (guest / member / saved) — bind them all.
      this.querySelectorAll('[data-ob-fit-save]').forEach((button) => {
        button.addEventListener('click', async () => {
          button.disabled = true;
          const result = await Fit.saveToAccount();
          button.disabled = false;
          this.reportSave(result);
        });
      });

      this.querySelectorAll('[data-ob-fit-close]').forEach((button) => {
        button.addEventListener('click', () => Fit.close());
      });
    }

    /* ---------------------------------------------------------------- view */

    /** Push slider values into the shared profile, then re-render. */
    commit() {
      // A picker configured for one garment type does not render the other's
      // sliders, so an absent input means "don't touch that measurement" —
      // that is what stops a bottoms-only picker recalculating a top size.
      const patch = {};
      if (this.inputs.height) patch.height = this.inputs.height.value;
      if (this.inputs.weight) patch.weight = this.inputs.weight.value;
      if (this.inputs.waist) patch.waist = this.inputs.waist.value;
      Fit.setProfile(patch, 'input');
      this.render(Fit.getProfile());
    }

    /** Pull the shared profile into the sliders, then re-render. */
    hydrate() {
      const profile = Fit.getProfile();
      if (this.inputs.height) this.inputs.height.value = profile.height;
      if (this.inputs.weight) this.inputs.weight.value = profile.weight;
      if (this.inputs.waist) this.inputs.waist.value = profile.waist;
      this.render(profile);
    }

    render(profile) {
      const { units } = profile;

      this.paintTracks();

      this.setText('[data-ob-fit-value="height"]', Fit.formatHeight(profile.height, units));
      this.setText('[data-ob-fit-value="weight"]', Fit.formatWeight(profile.weight, units));
      this.setText('[data-ob-fit-value="waist"]', Fit.formatWaist(profile.waist, units));

      this.setText('[data-ob-fit-result="top"]', profile.top);
      this.setText('[data-ob-fit-result="bottom"]', profile.bottom);
      this.setText('[data-ob-fit-range]', Fit.formatWaistRange(profile.bottom, units));
      this.setText('[data-ob-fit-unit-label="waist"]', Fit.unitLabels[units].waist);

      // Rewrite the A–H chart from the engine so it can never disagree with the
      // recommendation, and so it follows the metric/imperial toggle.
      Fit.BOTTOMS_RANGES.forEach(({ size }) => {
        this.setText(`[data-ob-fit-chart="${size}"]`, Fit.formatWaistRange(size, units));
      });

      const note = this.querySelector('[data-ob-fit-note]');
      if (note) {
        const text = this.getAttribute(`data-note-size-${String(profile.top).replace('-', 'minus-')}`);
        if (text) note.textContent = text;
      }

      // Slider end captions follow the unit toggle.
      this.setText('[data-ob-fit-scale="height-min"]', Fit.formatHeight(Fit.LIMITS.height.min, units));
      this.setText('[data-ob-fit-scale="height-max"]', Fit.formatHeight(Fit.LIMITS.height.max, units));
      this.setText('[data-ob-fit-scale="weight-min"]', Fit.formatWeight(Fit.LIMITS.weight.min, units));
      this.setText('[data-ob-fit-scale="weight-max"]', Fit.formatWeight(Fit.LIMITS.weight.max, units));
      this.setText('[data-ob-fit-scale="waist-min"]', Fit.formatWaist(Fit.LIMITS.waist.min, units));
      this.setText('[data-ob-fit-scale="waist-max"]', Fit.formatWaist(Fit.LIMITS.waist.max, units));

      this.querySelectorAll('[data-ob-fit-units]').forEach((button) => {
        const active = button.dataset.obFitUnits === units;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      this.renderAccount(profile);
    }

    renderAccount(profile) {
      const state = Fit.config.loggedIn
        ? profile.savedToAccount
          ? 'saved'
          : 'member'
        : 'guest';

      this.querySelectorAll('[data-ob-fit-state]').forEach((zone) => {
        zone.hidden = zone.dataset.obFitState !== state;
      });

      // Nothing to save until the customer has actually moved a slider.
      this.querySelectorAll('[data-ob-fit-save]').forEach((button) => {
        button.hidden = !profile.captured;
      });
    }

    reportSave(result) {
      if (result.ok) {
        this.setStatus(this.dataset.savedMessage || 'Saved to your account.');
        this.hydrate();
        return;
      }
      const messages = {
        'not-logged-in': this.dataset.loginRequiredMessage || 'Log in to save your fit.',
        'no-endpoint': this.dataset.pendingMessage || "Your fit is saved on this device. Account sync isn't switched on yet.",
        network: this.dataset.errorMessage || "We couldn't save your fit just now. Please try again."
      };
      this.setStatus(messages[result.reason] || messages.network);
    }

    setStatus(message) {
      const status = this.querySelector('[data-ob-fit-status]');
      if (!status) return;
      status.textContent = message;
      status.hidden = !message;
    }

    /**
     * Bold Flame up to the handle, Crown Gold past it. WebKit has no
     * progress pseudo-element for a range input, so the filled part of the
     * track is a gradient stop the stylesheet reads off `--ob-fit-fill`.
     */
    paintTracks() {
      Object.values(this.inputs).forEach((input) => {
        if (!input) return;
        const min = Number(input.min);
        const max = Number(input.max);
        const value = Number(input.value);
        const filled = max > min ? ((value - min) / (max - min)) * 100 : 0;
        input.style.setProperty('--ob-fit-fill', `${Math.min(100, Math.max(0, filled))}%`);
      });
    }

    setText(selector, value) {
      this.querySelectorAll(selector).forEach((node) => {
        node.textContent = value;
      });
    }
  }

  window.customElements.define('fit-finder', FitFinder);

  /* ---------------------------------------------- global wiring, once only */

  /**
   * Open the picker the way the header does: whatever drawer the trigger lives
   * in closes first, then the popup opens once that animation is out of the
   * way. Every entry point goes through this, so they cannot drift apart.
   */
  const openFromTrigger = () => {
    document.querySelector('dialog-close-button')?.click();
    document.querySelector('.quick-buy-modal__close-button')?.click();
    window.setTimeout(Fit.open, 200);
  };

  // Header / mobile-nav "Find My Fit" entry points.
  const bindTriggers = () => {
    document.querySelectorAll('[data-js-element="find-my-fit"]').forEach((trigger) => {
      if (trigger.dataset.obFitBound === 'true') return;
      trigger.dataset.obFitBound = 'true';

      const link = trigger.matches('a') ? trigger : trigger.querySelector('a');
      trigger.addEventListener('click', (event) => {
        // When a fit is stored on the account, the button is a real link to it.
        if (link && link.dataset.obFitAccountLink === 'true') return;
        event.preventDefault();
        openFromTrigger();
      });
    });

    document.querySelectorAll('[data-js-element="close-popup"]').forEach((trigger) => {
      if (trigger.dataset.obFitBound === 'true') return;
      trigger.dataset.obFitBound = 'true';
      trigger.addEventListener('click', Fit.close);
    });

    document.querySelectorAll('[data-ob-fit-done]').forEach((trigger) => {
      if (trigger.dataset.obFitBound === 'true') return;
      trigger.dataset.obFitBound = 'true';
      trigger.addEventListener('click', done);
    });

    document.querySelectorAll('[data-ob-fit-lock]').forEach((trigger) => {
      if (trigger.dataset.obFitBound === 'true') return;
      trigger.dataset.obFitBound = 'true';
      trigger.addEventListener('click', lockIn);
    });
  };

  /**
   * The universal way in: a link to `#find-my-fit`.
   *
   * The header has its own markup and its own binding, which nothing outside
   * the header can reuse. This is the version for everywhere else — a rich text
   * link, a section's button URL, a campaign landing on
   * `/collections/all#find-my-fit` — anywhere the theme editor gives someone a
   * URL field and no way to add markup. Custom Liquid that can add markup can
   * use `data-ob-fit-open` on any element instead, link or not.
   *
   * Both behave exactly like the header trigger: drawers close, the picker
   * opens, and the shopper stays on the page they were on.
   */
  const FIT_HASHES = ['#find-my-fit', '#fit-finder'];

  const isFitHash = (hash) => FIT_HASHES.includes(String(hash || '').toLowerCase());

  const fitTriggerFrom = (target) => {
    const element = target && target.closest ? target.closest('a[href], [data-ob-fit-open]') : null;
    if (!element) return null;

    // The header's own triggers bind themselves, and may be a real link to the
    // account page once a fit is stored there.
    if (element.closest('[data-js-element="find-my-fit"]')) return null;
    if (element.hasAttribute('data-ob-fit-open')) return element;

    try {
      return isFitHash(new URL(element.getAttribute('href'), window.location.href).hash) ? element : null;
    } catch (error) {
      return null;
    }
  };

  document.addEventListener('click', (event) => {
    // A modifier click is the shopper asking for a new tab or window; let the
    // browser have it, and the hash opens the picker over there instead.
    if (event.defaultPrevented || event.button > 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!fitTriggerFrom(event.target)) return;
    event.preventDefault();
    openFromTrigger();
  });

  /**
   * The same link followed from another page: the browser lands here with the
   * hash, so the picker opens on arrival. The hash is dropped on the way — it
   * is an instruction, not a place, and leaving it would reopen the picker on
   * every refresh and on the way back through history.
   */
  const openFromHash = () => {
    if (!isFitHash(window.location.hash)) return;
    if (window.history && window.history.replaceState) {
      window.history.replaceState({}, '', window.location.pathname + window.location.search);
    }
    Fit.open();
  };

  /**
   * Bank what the picker is showing.
   *
   * A shopper who read the recommendation and pressed the button that ends the
   * picker has answered, even if they never moved a slider — the sliders start
   * on a real fit and the popup has been telling them their size the whole
   * time. Without this the profile stays uncaptured: nothing is written to
   * storage, the header keeps saying "Find My Fit" and the cart has no size to
   * select. Dismissing the picker — the X, the overlay, Escape — is not an
   * answer and still saves nothing.
   */
  const commitPopup = () => {
    const picker = document.querySelector('popup-size fit-finder');
    if (picker && picker.commit) picker.commit();
  };

  /**
   * The way out of the picker with no intent behind it: keep the fit, close the
   * popup, and leave the shopper on the page they were reading (OB-264).
   */
  const done = () => {
    commitPopup();
    Fit.close();
  };

  /**
   * "Lock in My Size" — the way out of the picker when the cart's gift offer
   * opened it.
   *
   * Whoever opened the picker listens for the event and does the returning —
   * this file knows nothing about carts.
   */
  const lockIn = () => {
    commitPopup();

    const profile = Fit.getProfile();
    document.dispatchEvent(
      new CustomEvent('onebone:fit:lock', {
        detail: { intent: Fit.getIntent(), top: profile.top, bottom: profile.bottom }
      })
    );
    Fit.close();
  };

  /**
   * Show the one way out that matches why the picker was opened. Both are in
   * the markup; exactly one is ever visible.
   */
  const renderPopupIntent = (intent) => {
    document.querySelectorAll('[data-ob-fit-popup-action]').forEach((action) => {
      const wanted = intent === 'gift' ? 'gift' : 'default';
      action.hidden = action.dataset.obFitPopupAction !== wanted;
    });
  };

  /**
   * Every "Find my fit" trigger — header, mobile nav, PDP — shows the stored fit
   * once there is one, and falls back to its own default label.
   *
   * `data-ob-fit-label-scope` decides what a trigger is allowed to show:
   * `top` or `bottom` for a single-garment surface such as a PDP, `both`
   * elsewhere. A half the customer has not given us is never shown, so a
   * default recommendation is not mistaken for their answer.
   */
  const fitLabelFor = (label, profile) => {
    const scope = label.dataset.obFitLabelScope || 'both';
    const parts = [];
    if (scope !== 'bottom' && profile.hasTop) parts.push(profile.top);
    if (scope !== 'top' && profile.hasBottom) parts.push(profile.bottom);
    if (!parts.length) return null;
    return `${label.dataset.obFitLabelPrefix || 'My fit'}: ${parts.join(' / ')}`;
  };

  const renderTriggerLabels = () => {
    const profile = Fit.getProfile();
    document.querySelectorAll('[data-ob-fit-label]').forEach((label) => {
      const link = label.closest('a');
      const stored = fitLabelFor(label, profile);

      label.textContent = stored || label.dataset.obFitLabel;
      label.classList.toggle('is-set', Boolean(stored));

      if (!link) return;
      // Only send them to the account page when the fit is genuinely stored
      // there; otherwise the trigger opens the picker.
      const toAccount = Boolean(stored) && Fit.config.loggedIn && profile.savedToAccount;
      link.href = toAccount ? Fit.config.accountUrl + '#fit-profile' : '#';
      link.dataset.obFitAccountLink = toAccount ? 'true' : 'false';
    });
  };

  // The account page renders a read-only summary outside any <fit-finder>, so
  // it needs its own pass.
  const renderProfileSummary = () => {
    const summary = document.querySelector('[data-ob-fit-profile]');
    if (!summary) return;

    const profile = Fit.getProfile();
    const empty = document.querySelector('[data-ob-fit-profile-empty]');

    summary.hidden = !profile.captured;
    if (empty) empty.hidden = profile.captured;
    if (!profile.captured) return;

    const write = (selector, value) => {
      summary.querySelectorAll(selector).forEach((node) => {
        node.textContent = value;
      });
    };
    write('[data-ob-fit-result="top"]', profile.top);
    write('[data-ob-fit-result="bottom"]', profile.bottom);
    write('[data-ob-fit-value="height"]', Fit.formatHeight(profile.height, profile.units));
    write('[data-ob-fit-value="weight"]', Fit.formatWeight(profile.weight, profile.units));
    write('[data-ob-fit-value="waist"]', Fit.formatWaist(profile.waist, profile.units));
  };

  // Any element tagged with a size letter gets its waist range written from the
  // engine, wherever it lives — the A–H chart, the room shopper's select.
  const renderChartLabels = () => {
    const units = Fit.getProfile().units;
    const suffix = Fit.unitLabels[units].waist;
    Fit.BOTTOMS_RANGES.forEach(({ size }) => {
      document.querySelectorAll(`[data-ob-fit-chart="${size}"]`).forEach((node) => {
        // Inside the picker's own chart the unit sits in the column heading.
        const bare = node.closest('fit-finder');
        node.textContent = bare
          ? Fit.formatWaistRange(size, units)
          : `${Fit.formatWaistRange(size, units)} ${suffix}`;
      });
    });
  };

  /**
   * Preselect the stored size once the PDP's variant picker can hear about it.
   *
   * The picker is an Alpine component: it binds the `change` listener that
   * syncs price, media, URL and the add-to-cart form when Alpine walks the DOM,
   * and Alpine's bundle loads after this file. Ticking the radio before that
   * would leave the rest of the page on the old variant, so the page-land apply
   * waits for `alpine:initialized`. `load` is the fallback for a page that
   * never boots Alpine, or for this file arriving after Alpine already ran.
   */
  const applyToProductOnLoad = () => {
    let applied = false;
    const apply = () => {
      if (applied) return;
      applied = true;
      Fit.applyToProduct({ initial: true });
    };

    if (document.readyState === 'complete') {
      apply();
      return;
    }
    document.addEventListener('alpine:initialized', apply, { once: true });
    window.addEventListener('load', apply, { once: true });
  };

  const start = () => {
    bindTriggers();
    openFromHash();
    renderTriggerLabels();
    renderProfileSummary();
    renderChartLabels();
    applyToProductOnLoad();

    // Okendo's sizing quiz drops raw answers in localStorage and fires this.
    document.addEventListener('customOkendoQuiz', () => {
      const parseLow = (value) => {
        const match = String(value || '').match(/\d+/);
        return match ? Number(match[0]) : NaN;
      };
      const feet = String(window.localStorage?.getItem('okeheight') || '').split('.');
      const patch = {};
      if (feet.length === 2) patch.height = Number(feet[0]) * 12 + Number(feet[1]);
      const weight = parseLow(window.localStorage?.getItem('okeweight'));
      const waist = parseLow(window.localStorage?.getItem('okewaist'));
      if (Number.isFinite(weight)) patch.weight = weight;
      if (Number.isFinite(waist)) patch.waist = waist;
      if (Object.keys(patch).length) Fit.setProfile(patch, 'okendo');
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('onebone:fit:change', () => {
    renderTriggerLabels();
    renderProfileSummary();
    renderChartLabels();
    Fit.applyToProduct();
  });
  document.addEventListener('shopify:section:load', bindTriggers);
  window.addEventListener('hashchange', openFromHash);
  document.addEventListener('onebone:fit:open', (event) => renderPopupIntent(event.detail?.intent));
})();
